import { Dialog } from "@headlessui/react";
import {
  Armchair,
  Gem,
  Home,
  PiggyBank,
  Scale,
  TrendingUp,
} from "lucide-react";
import { Modal } from "@unlikelyai-magic/ui/modals";
import { cn } from "@unlikelyai-magic/utils";
import { getPersonasForOrganisation } from "./personas";
import type { Persona } from "./types";

const ICON_MAP: Record<string, React.ElementType> = {
  PiggyBank,
  TrendingUp,
  Armchair,
  Scale,
  Gem,
  Home,
};

interface SimulateModalProps {
  onClose: () => void;
  onSelectPersona: (persona: Persona) => void;
  tenantId?: string;
}

export const SimulateModal = ({
  onClose,
  onSelectPersona,
  tenantId,
}: SimulateModalProps) => {
  const personas = getPersonasForOrganisation(tenantId);
  return (
    <Dialog open={true} onClose={onClose}>
      <div className="fixed inset-0 bg-black/30 z-40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center z-50">
        <Modal className="w-[640px] max-h-[80vh] flex flex-col">
          <Modal.Header title="Choose a Persona" onClose={onClose} />
          <Modal.Content className="flex-1 min-h-0 overflow-y-auto p-6">
            <p className="text-sm text-brand-muted mb-5">
              Select a persona for the simulated user. The AI will role-play as
              this customer and have a conversation with the agent.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {personas.map((persona) => {
                const IconComponent = ICON_MAP[persona.icon];
                return (
                  <button
                    key={persona.id}
                    type="button"
                    onClick={() => onSelectPersona(persona)}
                    className={cn(
                      "flex items-start gap-3 rounded-lg border border-brand-border p-4 text-left transition-all",
                      "hover:border-brand-primary hover:bg-brand-primary/5",
                      "focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                    )}
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-primary/10 text-brand-primary">
                      {IconComponent && <IconComponent className="h-5 w-5" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-brand-foreground">
                        {persona.name}
                      </p>
                      <p className="mt-0.5 text-xs text-brand-muted line-clamp-2">
                        {persona.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </Modal.Content>
        </Modal>
      </div>
    </Dialog>
  );
};
